import type {
  MapDevice,
  MapDevicePatch,
  MapHub,
  MapHubPatch,
  MapLink,
  MapLinkPatch,
  StatusAlert,
  StatusMapChanges,
  StatusMapSnapshot
} from './statusTelemetryApi';

export class StatusMapDeltaRecoveryError extends Error {}

type Identified = { id: string };

const fail = (message: string): never => {
  throw new StatusMapDeltaRecoveryError(message);
};

const revisionNumber = (value: string, label: string) => {
  const parsed = Number(value);
  if (!/^\d+$/.test(value) || !Number.isSafeInteger(parsed) || parsed < 0) {
    fail(`Invalid ${label}`);
  }
  return parsed;
};

const assertUnique = (ids: string[], label: string) => {
  if (new Set(ids).size !== ids.length) fail(`Duplicate ${label}`);
};

const assertDisjoint = (sets: Array<{ label: string; ids: string[] }>) => {
  const owners = new Map<string, string>();
  sets.forEach(set => {
    assertUnique(set.ids, set.label);
    set.ids.forEach(id => {
      const owner = owners.get(id);
      if (owner) fail(`Conflicting ${owner}/${set.label} operation for ${id}`);
      owners.set(id, set.label);
    });
  });
};

const reconcileCollection = <T extends Identified, P extends Identified>(
  current: T[],
  upserts: T[],
  patches: P[],
  removed: string[],
  label: string,
  applyPatch: (entity: T, patch: P) => T
): T[] => {
  if (!upserts.length && !patches.length && !removed.length) return current;

  assertDisjoint([
    { label: `${label} upsert`, ids: upserts.map(item => item.id) },
    { label: `${label} patch`, ids: patches.map(item => item.id) },
    { label: `${label} removal`, ids: removed }
  ]);

  const existing = new Map(current.map(entity => [entity.id, entity]));
  patches.forEach(patch => {
    if (!existing.has(patch.id)) fail(`Unknown ${label} patch target ${patch.id}`);
  });

  const removedIDs = new Set(removed);
  const upsertByID = new Map(upserts.map(entity => [entity.id, entity]));
  const patchByID = new Map(patches.map(patch => [patch.id, patch]));
  const next = current.flatMap(entity => {
    if (removedIDs.has(entity.id)) return [];
    const replacement = upsertByID.get(entity.id);
    if (replacement) return [replacement];
    const patch = patchByID.get(entity.id);
    return [patch ? applyPatch(entity, patch) : entity];
  });

  upserts
    .filter(entity => !existing.has(entity.id))
    .sort((left, right) => left.id.localeCompare(right.id))
    .forEach(entity => next.push(entity));
  return next;
};

const reconcileAlerts = (
  current: StatusAlert[],
  upserts: StatusAlert[],
  removed: string[]
): StatusAlert[] => {
  if (!upserts.length && !removed.length) return current;
  const upsertIDs = upserts.map(alert => alert.alert_id);
  assertDisjoint([
    { label: 'alert upsert', ids: upsertIDs },
    { label: 'alert removal', ids: removed }
  ]);
  const existingIDs = new Set(current.map(alert => alert.alert_id));
  const removedIDs = new Set(removed);
  const upsertByID = new Map(upserts.map(alert => [alert.alert_id, alert]));
  const next = current.flatMap(alert => {
    if (removedIDs.has(alert.alert_id)) return [];
    return [upsertByID.get(alert.alert_id) ?? alert];
  });
  upserts
    .filter(alert => !existingIDs.has(alert.alert_id))
    .sort((left, right) => left.alert_id.localeCompare(right.alert_id))
    .forEach(alert => next.push(alert));
  return next;
};

const reconcileAlertCollections = (
  active: StatusAlert[],
  unverified: StatusAlert[],
  upserts: StatusAlert[],
  removed: string[]
) => {
  if (!upserts.length && !removed.length) {
    return { activeAlerts: active, unverifiedAlerts: unverified };
  }
  const reconciled = reconcileAlerts([...active, ...unverified], upserts, removed);
  return {
    activeAlerts: reconciled.filter(alert => alert.verification_state !== 'unverified'),
    unverifiedAlerts: reconciled.filter(alert => alert.verification_state === 'unverified')
  };
};

const patchHub = (hub: MapHub, patch: MapHubPatch): MapHub => ({
  ...hub,
  alert_summary: patch.alert_summary ?? hub.alert_summary,
  device_health_summary:
    patch.device_health_summary === null ? undefined : patch.device_health_summary
});

const patchDevice = (device: MapDevice, patch: MapDevicePatch): MapDevice => ({
  ...device,
  reachability: patch.reachability,
  collection_health: patch.collection_health,
  freshness: patch.freshness,
  alert_summary: patch.alert_summary ?? device.alert_summary
});

const patchLink = (link: MapLink, patch: MapLinkPatch): MapLink => ({
  ...link,
  state: patch.state,
  freshness: patch.freshness,
  updated_at: patch.updated_at,
  alert_summary: patch.alert_summary ?? link.alert_summary
});

export const applyStatusMapChanges = (
  snapshot: StatusMapSnapshot,
  delta: StatusMapChanges
): StatusMapSnapshot => {
  if (delta.reload_required) fail('Map delta requires snapshot recovery');
  if (delta.tenant_id !== snapshot.tenant_id) fail('Map delta tenant mismatch');
  if (delta.base_revision !== snapshot.revision) fail('Map delta base revision mismatch');

  const baseRevision = revisionNumber(delta.base_revision, 'base revision');
  const revision = revisionNumber(delta.revision, 'revision');
  const currentRevision = revisionNumber(delta.current_revision, 'current revision');
  const earliestRevision = revisionNumber(delta.earliest_revision, 'earliest revision');
  if (revision < baseRevision || currentRevision < revision || earliestRevision > baseRevision + 1) {
    fail('Map delta revision gap');
  }
  if (delta.has_more && revision === baseRevision) fail('Map delta pagination made no progress');
  if (!delta.has_more && revision !== currentRevision) fail('Map delta stopped before current revision');
  const hasChanges =
    Object.values(delta.upserts).some(items => items.length > 0) ||
    Object.values(delta.patches).some(items => items.length > 0) ||
    Object.values(delta.removed).some(items => items.length > 0) ||
    Object.keys(delta.freshness).length > 0 ||
    delta.observability !== undefined ||
    delta.alert_totals !== undefined;
  if (revision === baseRevision && hasChanges) fail('Map delta changes did not advance revision');

  const hubs = reconcileCollection(
    snapshot.hubs,
    delta.upserts.hubs,
    delta.patches.hubs,
    delta.removed.hubs,
    'hub',
    patchHub
  );
  const devices = reconcileCollection(
    snapshot.devices,
    delta.upserts.devices,
    delta.patches.devices,
    delta.removed.devices,
    'device',
    patchDevice
  );
  const links = reconcileCollection(
    snapshot.links,
    delta.upserts.links,
    delta.patches.links,
    delta.removed.links,
    'link',
    patchLink
  );
  const { activeAlerts, unverifiedAlerts } = reconcileAlertCollections(
    snapshot.active_alerts,
    snapshot.unverified_alerts,
    delta.upserts.alerts,
    delta.removed.alerts
  );
  const freshness = Object.keys(delta.freshness).length
    ? { ...snapshot.freshness, ...delta.freshness }
    : snapshot.freshness;
  const observability = delta.observability === undefined
    ? snapshot.observability
    : delta.observability ?? { grafana_available: false };

  return {
    ...snapshot,
    server_time: delta.server_time,
    revision: delta.revision,
    hubs,
    devices,
    links,
    active_alerts: activeAlerts,
    unverified_alerts: unverifiedAlerts,
    alert_totals: delta.alert_totals ?? snapshot.alert_totals,
    freshness,
    observability
  };
};
